import React, { useMemo, useRef, useState } from "react";
import StaticPopupNew from "components/StaticPopupNew";

const getFormControlDisplayValue = (sourceControl) => {
  const tagName = sourceControl.tagName;
  const type = sourceControl.type;

  if (type === "hidden") return null;

  if (type === "checkbox" || type === "radio") {
    return sourceControl.checked ? "Yes" : "No";
  }

  if (type === "file") {
    const files = Array.from(sourceControl.files || []);
    return files.length
      ? files.map((file) => file.name).join(", ")
      : "No file selected";
  }

  if (tagName === "SELECT") {
    const selectedOptions = Array.from(sourceControl.selectedOptions || []);
    return (
      selectedOptions.map((option) => option.textContent?.trim()).join(", ") ||
      sourceControl.value ||
      "-"
    );
  }

  return sourceControl.value || sourceControl.textContent || "-";
};

const replaceFormControlsWithReadOnlyText = (sourceNode, clonedNode) => {
  const sourceControls = sourceNode.querySelectorAll("input, textarea, select");
  const clonedControls = clonedNode.querySelectorAll("input, textarea, select");

  sourceControls.forEach((sourceControl, index) => {
    const clonedControl = clonedControls[index];
    if (!clonedControl) return;

    const displayValue = getFormControlDisplayValue(sourceControl);

    if (displayValue === null) {
      clonedControl.remove();
      return;
    }

    const staticNode = document.createElement(
      sourceControl.tagName === "TEXTAREA" ? "div" : "span"
    );
    staticNode.className = "quotation-pdf-static-field";
    staticNode.textContent = displayValue;

    clonedControl.replaceWith(staticNode);
  });
};

const buildPreviewDocument = (targetNode, title) => {
  const clonedNode = targetNode.cloneNode(true);
  replaceFormControlsWithReadOnlyText(targetNode, clonedNode);

  clonedNode.querySelectorAll(".quotation-pdf-ignore").forEach((node) => {
    node.remove();
  });

  const styles = Array.from(
    document.querySelectorAll('link[rel="stylesheet"], style')
  )
    .map((node) => node.outerHTML)
    .join("\n");

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${title}</title>
    ${styles}
    <style>
      html, body {
        background: #ffffff !important;
        color: #111827;
        font-family: Arial, sans-serif;
      }
      body {
        margin: 0;
        padding: 24px;
      }
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      .quotation-pdf-root {
        max-width: 1200px;
        margin: 0 auto;
      }
      .quotation-pdf-static-field {
        display: inline-block;
        min-width: 48px;
        max-width: 100%;
        min-height: 28px;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        background: #f9fafb;
        color: #111827;
        padding: 6px 8px;
        line-height: 1.35;
        white-space: pre-wrap;
        overflow-wrap: anywhere;
        vertical-align: top;
      }
      @page {
        size: A4;
        margin: 12mm;
      }
    </style>
  </head>
  <body>
    <div class="quotation-pdf-root">${clonedNode.outerHTML}</div>
  </body>
</html>`;
};

const QuotationPdfPreviewButton = ({
  targetRef,
  rfqData,
  buttonClassName = "",
}) => {
  const iframeRef = useRef(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");

  const fileName = useMemo(() => {
    const rfqId = rfqData?.rfqId || "quotation";
    const rfqName = String(rfqData?.name || "quotation")
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase();
    return `${rfqName || "quotation"}-${rfqId}.pdf`;
  }, [rfqData?.name, rfqData?.rfqId]);

  const handleOpenPreview = () => {
    if (!targetRef?.current) return;

    setPreviewHtml(
      buildPreviewDocument(
        targetRef.current,
        `Quotation Preview - RFQ ${rfqData?.rfqId || ""}`
      )
    );
    setOpenPreview(true);
  };

  const handleDownloadPdf = () => {
    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow) return;

    iframeWindow.document.title = fileName;
    iframeWindow.focus();
    iframeWindow.print();
  };

  return (
    <>
      {/* <button
        type="button"
        className={`quotation-pdf-ignore inline-flex items-center justify-center gap-2 rounded-lg border border-[#313119] bg-white px-3 py-2 text-xs font-semibold text-[#313119] transition hover:bg-[#F5F5F3] ${buttonClassName}`}
        onClick={handleOpenPreview}
      >
        Preview / Download PDF
      </button> */}

      <StaticPopupNew
        header="Quotation PDF Preview"
        width="7xl"
        bodyStyle="p-0"
        bodyOverflow="overflow-hidden"
        openModal={openPreview}
        handleClose={() => setOpenPreview(false)}
      >
        <div className="quotation-pdf-ignore flex items-center justify-end gap-2 border-b border-gray-200 bg-white px-4 py-2">
          <button
            type="button"
            className="rounded-lg bg-[#313119] px-4 py-2 text-xs font-semibold text-white hover:bg-[#45452A]"
            onClick={handleDownloadPdf}
          >
            Download PDF
          </button>
        </div>
        <iframe
          ref={iframeRef}
          title="Quotation PDF Preview"
          srcDoc={previewHtml}
          className="h-[78vh] w-full bg-white"
        />
      </StaticPopupNew>
    </>
  );
};

export default QuotationPdfPreviewButton;
